import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Eye, EyeOff, CheckCircle, Tag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AddressModal from '../components/AddressModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promoFromUrl = searchParams.get('promo') || '';
  const tierFromUrl = searchParams.get('tier') || '';
  const { login, mode } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    promo_code: promoFromUrl
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPromo, setShowPromo] = useState(!!promoFromUrl);
  const [loading, setLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);

  const TIER_MAP = {
    'full-package': { plan: 'full_package', label: 'Full Package ($675)' },
    'accelerated': { plan: 'accelerated', label: 'Accelerated ($825)' },
    'monthly': { plan: 'monthly', label: 'Monthly ($75/mo)' },
  };

  const selectedTier = TIER_MAP[tierFromUrl] || null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        mode: 'owner',
        promo_code: formData.promo_code || null
      });

      login(response.data);

      if (formData.promo_code) {
        toast.success('Welcome to Edu Paws! All 10 boxes unlocked with your promo code!');
        navigate('/owner/dogs');
        return;
      }

      // If a tier was selected from the quiz, show address modal before checkout
      if (selectedTier) {
        toast.success('Account created! One more step: your shipping address.');
        setPendingUserId(response.data.id);
        setShowAddressModal(true);
        return;
      }

      toast.success('Welcome to Edu Paws!');
      navigate('/owner/dogs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if trying to access signup in admin mode
  if (mode === 'admin') {
    navigate('/');
    return null;
  }

  const handleAddressSubmit = async (shippingAddress, promoCode) => {
    setLoading(true);
    try {
      if (selectedTier.plan !== 'monthly') {
        const checkoutRes = await axios.post(`${API}/checkout/package`, {
          user_id: pendingUserId,
          plan: selectedTier.plan,
          origin_url: window.location.origin,
          shipping_address: shippingAddress,
          promo_code: promoCode
        });
        if (checkoutRes.data.url) {
          window.location.href = checkoutRes.data.url;
          return;
        }
      } else {
        const seriesRes = await axios.get(`${API}/series`);
        const box1 = seriesRes.data.find(s => s.order_index === 1);
        if (box1) {
          const checkoutRes = await axios.post(`${API}/subscriptions/create`, {
            user_id: pendingUserId,
            series_id: box1.id,
            origin_url: window.location.origin,
            shipping_address: shippingAddress,
            promo_code: promoCode
          });
          if (checkoutRes.data.url) {
            window.location.href = checkoutRes.data.url;
            return;
          }
        }
      }
      toast.error('Could not start checkout. You can subscribe from the Training page.');
      navigate('/owner/series');
    } catch (err) {
      toast.error('Could not start checkout. You can subscribe from the Training page.');
      navigate('/owner/series');
    } finally {
      setLoading(false);
      setShowAddressModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-edupaws-sage relative overflow-hidden">
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <img 
            src="https://edupaws.co/cdn/shop/files/EduPaws_Dog.png?v=1759709026&width=400" 
            alt="Edu Paws Dog" 
            className="w-64 mb-8"
          />
          <h1 className="font-heading text-4xl italic font-bold text-white text-center mb-4">
            Join Thousands of Happy Pups!
          </h1>
          <div className="space-y-3 text-left max-w-xs">
            {['10-month transformation curriculum', 'Step-by-step video lessons', 'Track your progress', 'Monthly boxes delivered'].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 text-white/90 font-body">
                <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="p-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-edupaws-navy transition-colors font-body"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
          <img 
            src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" 
            alt="Edu Paws" 
            className="h-10"
          />
        </header>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <h2 className="font-heading text-3xl italic font-bold text-edupaws-navy mb-2">
              Start Your Journey
            </h2>
            <p className="text-gray-500 font-body mb-8">
              Create an account to begin your transformation
            </p>

            {/* Selected Tier Banner */}
            {selectedTier && (
              <div data-testid="tier-banner" className="mb-6 p-4 rounded-xl bg-edupaws-sage/10 border border-edupaws-sage/20">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-edupaws-sage flex-shrink-0" />
                  <div>
                    <p className="font-body font-bold text-edupaws-navy text-sm">Selected Plan: {selectedTier.label}</p>
                    <p className="font-body text-xs text-gray-500">You'll be redirected to checkout after signing up</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-edupaws-navy font-body font-medium">
                  Your Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="name-input"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="mt-1.5 h-12 bg-gray-50 border-gray-200 rounded-xl font-body focus:border-edupaws-sage focus:ring-edupaws-sage"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-edupaws-navy font-body font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  data-testid="email-input"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="mt-1.5 h-12 bg-gray-50 border-gray-200 rounded-xl font-body focus:border-edupaws-sage focus:ring-edupaws-sage"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-edupaws-navy font-body font-medium">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    name="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password"
                    required
                    minLength={6}
                    className="h-12 bg-gray-50 border-gray-200 rounded-xl pr-12 font-body focus:border-edupaws-sage focus:ring-edupaws-sage"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-edupaws-navy font-body font-medium">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  data-testid="confirm-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  className="mt-1.5 h-12 bg-gray-50 border-gray-200 rounded-xl font-body focus:border-edupaws-sage focus:ring-edupaws-sage"
                />
              </div>

              {/* Promo Code */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowPromo(!showPromo)}
                  data-testid="toggle-promo-btn"
                  className="flex items-center gap-2 text-edupaws-sage font-body font-medium text-sm hover:text-edupaws-navy transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  {showPromo ? 'Hide promo code' : 'Have a promo code?'}
                </button>
                {showPromo && (
                  <div className="mt-2">
                    <Input
                      name="promo_code"
                      data-testid="promo-code-input"
                      type="text"
                      value={formData.promo_code}
                      onChange={handleChange}
                      placeholder="Enter promo code"
                      className="h-12 bg-edupaws-sage/5 border-edupaws-sage/30 rounded-xl font-body focus:border-edupaws-sage focus:ring-edupaws-sage uppercase"
                    />
                    <p className="text-xs text-gray-400 font-body mt-1">Promo code will unlock all 10 training boxes</p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                data-testid="signup-submit"
                disabled={loading}
                className="w-full bg-edupaws-navy text-white font-semibold py-3 px-6 rounded-full
                           shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px] 
                           transition-all font-body disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>

            <p className="mt-8 text-center text-gray-500 font-body">
              Already have an account?{' '}
              <Link to="/login" className="text-edupaws-sage font-medium hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
      <AddressModal
        open={showAddressModal}
        onClose={() => { setShowAddressModal(false); navigate('/owner/dogs'); }}
        onSubmit={handleAddressSubmit}
        loading={loading}
      />
    </div>
  );
}
